import { useState, memo } from 'react';
import { useCopy } from '../lib/copy';

/**
 * FeedbackButtons - Thumbs-up/down feedback with richer thumbs-down experience.
 *
 * Interaction flow:
 * 1. User sees subtle thumbs up/down icons
 * 2. User clicks one -> immediate feedback recorded
 * 3. Thumbs down: expanded panel with textarea, contact opt-in, and email (if anonymous)
 * 4. Submit sends reason + contact preferences
 *
 * @param {string} requestId - The trace request_id for this response
 * @param {string} clientId - The client ID
 * @param {boolean} isAuthenticated - Whether the member is logged in
 * @param {function} onFeedback - Callback function(requestId, clientId, feedback, reason, extras)
 */
function FeedbackButtons({ requestId, clientId, isAuthenticated = false, onFeedback }) {
  const t = useCopy();
  const [feedback, setFeedback] = useState(null);
  const [showReasonPanel, setShowReasonPanel] = useState(false);
  const [reason, setReason] = useState('');
  const [contactRequested, setContactRequested] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFeedbackClick = async (feedbackType) => {
    if (isSubmitting || feedback) return;

    setFeedback(feedbackType);
    setIsSubmitting(true);

    try {
      await onFeedback(requestId, clientId, feedbackType, '');

      if (feedbackType === 'thumbs_down') {
        setShowReasonPanel(true);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedback(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReasonSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const extras = {};
      if (contactRequested) {
        extras.contact_requested = true;
        if (!isAuthenticated && contactEmail.trim()) {
          extras.contact_email = contactEmail.trim();
        }
      }

      await onFeedback(requestId, clientId, feedback, reason.trim(), extras);
      setShowReasonPanel(false);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting reason:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setShowReasonPanel(false);
    setReason('');
    setContactRequested(false);
    setContactEmail('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        {/* Thumbs Up */}
        <button
          onClick={() => handleFeedbackClick('thumbs_up')}
          disabled={isSubmitting || feedback !== null}
          className={`group relative p-1.5 rounded-md transition-all ${
            feedback === 'thumbs_up'
              ? 'bg-green-50 text-green-600'
              : feedback === 'thumbs_down'
                ? 'opacity-40 cursor-not-allowed'
                : 'text-slate-400 hover:text-green-600 hover:bg-green-50/50'
          }`}
          aria-label={t('Good response')}
          title={t('Good response')}
        >
          <svg
            className="w-3.5 h-3.5"
            fill={feedback === 'thumbs_up' ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"
            />
          </svg>
        </button>

        {/* Thumbs Down */}
        <button
          onClick={() => handleFeedbackClick('thumbs_down')}
          disabled={isSubmitting || feedback !== null}
          className={`group relative p-1.5 rounded-md transition-all ${
            feedback === 'thumbs_down'
              ? 'bg-red-50 text-red-600'
              : feedback === 'thumbs_up'
                ? 'opacity-40 cursor-not-allowed'
                : 'text-slate-400 hover:text-red-600 hover:bg-red-50/50'
          }`}
          aria-label={t('Needs improvement')}
          title={t('Needs improvement')}
        >
          <svg
            className="w-3.5 h-3.5"
            fill={feedback === 'thumbs_down' ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"
            />
          </svg>
        </button>

        {/* Thumbs up confirmation */}
        {feedback === 'thumbs_up' && (
          <span className="ml-1 text-[10px] text-slate-500 animate-fadeIn">Thanks!</span>
        )}

        {/* Post-submit confirmation for thumbs down */}
        {submitted && !showReasonPanel && feedback === 'thumbs_down' && (
          <span className="ml-1 text-[10px] text-slate-500 animate-fadeIn">
            {t('Thank you — we’ll look into this.')}
          </span>
        )}
      </div>

      {/* Expanded feedback panel (thumbs down) */}
      {showReasonPanel && (
        <div className="animate-fadeIn rounded-lg border border-slate-200 bg-slate-50 p-3 max-w-md">
          <p className="text-xs text-slate-600 leading-relaxed mb-2">
            {t(
              'Sorry this wasn’t helpful. Could you tell us what went wrong? Our team reviews every piece of feedback to improve answers.',
            )}
          </p>

          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('What was wrong or missing?')}
            className="w-full text-xs px-2.5 py-2 rounded border border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 focus:outline-none resize-none bg-white"
            rows={3}
            autoFocus
            disabled={isSubmitting}
          />

          {/* Contact opt-in */}
          <label className="flex items-start gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={contactRequested}
              onChange={(e) => setContactRequested(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
              disabled={isSubmitting}
            />
            <span className="text-[11px] text-slate-500 leading-snug">
              {t('I’d like staff to follow up with me once this is addressed')}
            </span>
          </label>

          {/* Email field for anonymous users */}
          {contactRequested && !isAuthenticated && (
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder={t('Your email address')}
              className="w-full mt-2 text-xs px-2.5 py-1.5 rounded border border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 focus:outline-none bg-white"
              disabled={isSubmitting}
            />
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-2.5">
            <button
              onClick={handleDismiss}
              className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700"
              disabled={isSubmitting}
            >
              {t('Skip')}
            </button>
            <button
              onClick={handleReasonSubmit}
              disabled={isSubmitting}
              className="px-3 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSubmitting ? t('Sending...') : t('Send feedback')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(FeedbackButtons);
